import { Button } from "@/components/ui/button";
import { Download, ArrowRight } from "lucide-react";
import TryOnlineButton from "@/components/landing/TryOnlineButton";
import MiniTimer from "@/components/landing/MiniTimer";

export default function HeroSection({ downloadLink }) {
  const scrollToRegister = () => {
    document.getElementById("lead-capture")?.scrollIntoView({ behavior: "smooth" });
  };

  const trackDownload = () => {
    if (window.gtag) {
      window.gtag('event', 'click', { event_category: 'download', event_label: 'hero_download_btn' });
    }
  };

  const trackStartFree = () => {
    if (window.gtag) {
      window.gtag('event', 'click', { event_category: 'cta', event_label: 'hero_start_free_btn' });
    }
    scrollToRegister();
  };

  return (
    <section data-testid="hero-section" className="relative bg-white overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-green-50/80 to-white pointer-events-none" />
      <div className="relative container mx-auto px-4 md:px-6 max-w-5xl pt-12 pb-16 md:pt-20 md:pb-24">
        {/* Trust badge */}
        <div className="flex justify-center mb-6">
          <span
            data-testid="hero-trust-badge"
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-green-100 text-green-800 text-sm font-medium"
          >
            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            Built for Teachers. Made for Real School Work.
          </span>
        </div>

        {/* Headline */}
        <h1
          data-testid="hero-headline"
          className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 text-center max-w-3xl mx-auto leading-tight"
          style={{ fontFamily: "'Outfit', sans-serif" }}
        >
          Teachers, Stop Doing School Calculations{" "}
          <span className="text-green-700">Manually</span>
        </h1>

        {/* Subheadline */}
        <p
          data-testid="hero-subheadline"
          className="mt-5 text-base md:text-lg text-slate-600 text-center max-w-2xl mx-auto leading-relaxed"
        >
          Use <strong className="text-green-700">Teacher Utility Toolkit</strong> — One App That Solves Daily Teacher Tasks in Seconds.
        </p>

        {/* Description */}
        <p className="mt-3 text-sm md:text-base text-slate-500 text-center max-w-xl mx-auto">
          From <strong>Admission Eligibility Checking</strong> to <strong>MDM Calculation</strong>, everything you need is available in one simple mobile app.
        </p>

        {/* CTA Buttons */}
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
          <Button
            data-testid="hero-start-free-btn"
            onClick={trackStartFree}
            className="w-full sm:w-auto h-12 px-8 bg-green-700 hover:bg-green-800 text-white rounded-full text-base font-semibold shadow-lg shadow-green-900/20"
          >
            Start Free Now
            <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
          <Button
            data-testid="hero-download-btn"
            asChild
            variant="outline"
            className="w-full sm:w-auto h-12 px-8 border-2 border-green-700 text-green-700 hover:bg-green-50 rounded-full text-base font-semibold"
          >
            <a href={downloadLink} target="_blank" rel="noopener noreferrer" onClick={trackDownload}>
              <Download className="mr-2 w-4 h-4" />
              Download the App
            </a>
          </Button>
        </div>

        {/* Try Online - Clickbait */}
        <div className="mt-4 flex flex-col items-center gap-2">
          <MiniTimer />
          <TryOnlineButton />
        </div>
        <p className="mt-1.5 text-center text-xs text-orange-600 font-semibold animate-pulse">
          ⚡ ଏକ Click ରେ ସିଧା App ଖୋଲନ୍ତୁ — No Download, No Install!
        </p>

        {/* App mockup image */}
        <div className="mt-12 flex justify-center">
          <div className="relative w-64 md:w-72 rounded-3xl overflow-hidden shadow-2xl">
            <img
              src="https://customer-assets.emergentagent.com/job_odisha-teachers/artifacts/vydj4z1z_Emergent-03-12-2026_06_02_PM-removebg-preview.png"
              alt="Teacher Utility Toolkit App Screenshot"
              className="w-full h-auto"
              data-testid="hero-app-mockup"
              loading="eager"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
